import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from '../hooks/useInView';
import PrivacyModal from './PrivacyModal';

export default function Contact() {
  const [ref, isInView] = useInView({ threshold: 0.1 });
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefono: '',
    servizio: '',
    messaggio: '',
    privacy: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [privacyError, setPrivacyError] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.privacy) {
      setPrivacyError(true);
      return;
    }
    setPrivacyError(false);

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('nome', formData.nome);
      formBody.append('email', formData.email);
      formBody.append('telefono', formData.telefono);
      formBody.append('servizio', formData.servizio);
      formBody.append('messaggio', formData.messaggio);

      const response = await fetch('https://readdy.ai/api/form/d6k226m32h7pkqqstve0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nome: '',
          email: '',
          telefono: '',
          servizio: '',
          messaggio: '',
          privacy: false,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <section id="contatti" ref={ref} className="py-24 md:py-32 relative overflow-hidden px-4">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <div className="w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/294ef02ba4acd7fbf1dc9a85ab7c7006.jpg"
            alt="Ufficio Big Finance"
            className="w-full h-full object-cover object-top blur-sm"
          />
        </div>
        <div className="absolute inset-0 bg-white/85"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left Side - Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-dark mb-6">
              Iniziamo Insieme
            </h2>
            <p className="text-gray-dark text-lg mb-12">
              Contattaci per trasformare le tue idee in realtà
            </p>

            <div className="space-y-8">
              {/* Address */}
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full border-2 border-primary flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-primary text-xl"></i>
                </div>
                <div>
                  <div className="text-gray-medium text-sm uppercase mb-1">Indirizzo</div>
                  <div className="text-dark font-semibold">
                    Via Antonio Chinotto 1<br />
                    00195 Roma (RM), Italia
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full border-2 border-primary flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-primary text-xl"></i>
                </div>
                <div>
                  <div className="text-gray-medium text-sm uppercase mb-1">Email</div>
                  <a href="mailto:bigfinancesrl@gmail.com" className="text-dark font-semibold hover:text-primary transition-colors">
                    bigfinancesrl@gmail.com
                  </a>
                </div>
              </div>

              {/* Business Info */}
              <div className="pt-6 border-t border-gray-light">
                <div className="text-gray-medium text-sm space-y-1">
                  <div><strong>P.IVA:</strong> IT-06146231003</div>
                  <div><strong>REA:</strong> RM-1631943</div>
                </div>
              </div>

              {/* Social Icons */}
              <div className="flex gap-4 pt-6">
                <a
                  href="#"
                  className="w-12 h-12 rounded-full border-2 border-primary text-primary flex items-center justify-center hover:bg-primary hover:text-white transition-all"
                  aria-label="Facebook"
                >
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a
                  href="#"
                  className="w-12 h-12 rounded-full border-2 border-primary text-primary flex items-center justify-center hover:bg-primary hover:text-white transition-all"
                  aria-label="Instagram"
                >
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a
                  href="#"
                  className="w-12 h-12 rounded-full border-2 border-primary text-primary flex items-center justify-center hover:bg-primary hover:text-white transition-all"
                  aria-label="LinkedIn"
                >
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Right Side - Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-3xl shadow-xl p-8 md:p-10"
            >
              {/* Nome */}
              <div className="mb-6">
                <label htmlFor="nome" className="block text-gray-dark text-xs uppercase font-semibold mb-2">
                  Nome Completo
                </label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                  placeholder="Il tuo nome"
                  className="w-full px-4 py-3 border border-gray-light rounded-xl text-sm focus:outline-none focus:border-primary transition-colors"
                />
              </div>

              {/* Email */}
              <div className="mb-6">
                <label htmlFor="email" className="block text-gray-dark text-xs uppercase font-semibold mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="tua@email.com"
                  className="w-full px-4 py-3 border border-gray-light rounded-xl text-sm focus:outline-none focus:border-primary transition-colors"
                />
              </div>

              {/* Telefono */}
              <div className="mb-6">
                <label htmlFor="telefono" className="block text-gray-dark text-xs uppercase font-semibold mb-2">
                  Telefono
                </label>
                <input
                  type="tel"
                  id="telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  required
                  placeholder="+39 123 456 7890"
                  className="w-full px-4 py-3 border border-gray-light rounded-xl text-sm focus:outline-none focus:border-primary transition-colors"
                />
              </div>

              {/* Servizio */}
              <div className="mb-6">
                <label htmlFor="servizio" className="block text-gray-dark text-xs uppercase font-semibold mb-2">
                  Servizio di Interesse
                </label>
                <select
                  id="servizio"
                  name="servizio"
                  value={formData.servizio}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-light rounded-xl text-sm focus:outline-none focus:border-primary transition-colors cursor-pointer"
                >
                  <option value="">Seleziona un servizio</option>
                  <option value="Organizzazione Eventi">Organizzazione Eventi</option>
                  <option value="Campagne di Marketing">Campagne di Marketing</option>
                  <option value="Ricerche di Mercato">Ricerche di Mercato</option>
                  <option value="Eventi Fieristici">Eventi Fieristici</option>
                  <option value="Motorsport Events">Motorsport Events</option>
                  <option value="Campagne Pubblicitarie">Campagne Pubblicitarie</option>
                </select>
              </div>

              {/* Messaggio */}
              <div className="mb-6">
                <label htmlFor="messaggio" className="block text-gray-dark text-xs uppercase font-semibold mb-2">
                  Messaggio
                </label>
                <textarea
                  id="messaggio"
                  name="messaggio"
                  value={formData.messaggio}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="Raccontaci del tuo progetto..."
                  className="w-full px-4 py-3 border border-gray-light rounded-xl text-sm focus:outline-none focus:border-primary transition-colors resize-none"
                ></textarea>
                <div className="text-xs text-gray-medium mt-1 text-right">
                  {formData.messaggio.length}/500 caratteri
                </div>
              </div>

              {/* Privacy */}
              <div className="mb-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="privacy"
                    checked={formData.privacy}
                    onChange={(e) => {
                      handleChange(e);
                      if (e.target.checked) setPrivacyError(false);
                    }}
                    className={`mt-1 w-4 h-4 text-primary rounded focus:ring-primary cursor-pointer flex-shrink-0 ${privacyError ? 'border-red-500' : 'border-gray-light'}`}
                  />
                  <span className="text-xs text-gray-medium leading-relaxed">
                    Ho letto e accetto l&apos;
                    <button
                      type="button"
                      onClick={() => setIsPrivacyModalOpen(true)}
                      className="text-primary underline hover:text-primary/80 transition-colors cursor-pointer font-semibold"
                    >
                      Informativa sul Trattamento dei Dati Personali
                    </button>
                    {' '}ai sensi dell&apos;art. 13 del Reg. (UE) n. 2016/679 e autorizzo il trattamento dei miei dati personali. <span className="text-red-500">*</span>
                  </span>
                </label>
                {privacyError && (
                  <p className="mt-2 text-xs text-red-500 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    Devi leggere e accettare l&apos;informativa sulla privacy per continuare.
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary text-white py-4 rounded-full font-bold text-sm hover:bg-primary/90 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'INVIO IN CORSO...' : 'INVIA RICHIESTA'}
                {!isSubmitting && <i className="ri-arrow-right-line"></i>}
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl text-green-800 text-sm text-center">
                  Grazie! La tua richiesta è stata inviata con successo. Ti contatteremo presto.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-800 text-sm text-center">
                  Si è verificato un errore. Riprova più tardi o contattaci direttamente via email.
                </div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>

    <PrivacyModal
      isOpen={isPrivacyModalOpen}
      onClose={() => setIsPrivacyModalOpen(false)}
    />
    </>
  );
}